'use client'

import React, { useState } from 'react'
import { cn } from '../../../../../lib/utils'

import s from '../../../../styles/animations.module.css'
import { CheckboxComponent } from '../../../Checkbox'
import { Chip, type ChipProps } from '../../../Chip'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../Collapsible'
import Icon from '../../../Icon'

export type CheckboxTileChildProps = {
  id: string
  label: string
  description?: string
  isSelected: boolean
  disabled?: boolean
  handleClick?: () => void
  /** If true, this child will be selected/deselected when parent is toggled */
  propagateFromParent?: boolean
}

export type CheckboxTileProps = {
  id: string
  label: string
  description?: string
  isSelected: boolean
  disabled?: boolean
  tags?: ChipProps[]
  handleClick?: () => void
  children?: CheckboxTileChildProps[]
  /** Whether children section is expanded by default (default: false) */
  defaultExpanded?: boolean
  /** Called when parent is selected and should propagate to children */
  onSelectChildren?: (childIds: string[]) => void
  /** Called when parent is deselected and should propagate to children */
  onDeselectChildren?: (childIds: string[]) => void
}

export const CheckboxTile = (props: CheckboxTileProps) => {
  const {
    id,
    label,
    description,
    isSelected,
    disabled,
    tags,
    handleClick,
    children,
    defaultExpanded = false,
    onSelectChildren,
    onDeselectChildren,
  } = props

  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const handleParentClick = () => {
    if (disabled) return

    // Get children that should propagate (enabled and have propagateFromParent)
    const propagatableChildren =
      children?.filter((c) => !c.disabled && c.propagateFromParent) ?? []
    const propagatableChildIds = propagatableChildren.map((c) => c.id)

    if (!isSelected) {
      // Parent is being selected - select all propagatable children that aren't already selected
      const childrenToSelect = propagatableChildren
        .filter((c) => !c.isSelected)
        .map((c) => c.id)
      if (childrenToSelect.length > 0 && onSelectChildren) {
        onSelectChildren(childrenToSelect)
      }
    } else {
      // Parent is being deselected - deselect all propagatable children that are selected
      const childrenToDeselect = propagatableChildren
        .filter((c) => c.isSelected)
        .map((c) => c.id)
      if (childrenToDeselect.length > 0 && onDeselectChildren) {
        onDeselectChildren(childrenToDeselect)
      }
    }

    handleClick && handleClick()
  }

  return (
    <div
      className={cn(
        'rounded-lg flex p-2 flex flex-col w-full gap-2 border',
        {
          'bg-accent border-primary': isSelected,
          'border-transparent': !isSelected,
          'hover:bg-muted cursor-pointer': !disabled,
          'opacity-50 cursor-not-allowed': disabled,
        },
        s.smallTransitionIn,
      )}
      title={`list-tile-${id}`}
      onClick={(e) => {
        if (disabled) return
        // Prevent tile click when interacting with other button-like controls inside
        if ((e.target as HTMLElement).tagName === 'BUTTON') {
          return
        }
        // Prevent parent click when clicking on a child item or expand toggle
        if (
          (e.target as HTMLElement).closest('[data-child-tile]') ||
          (e.target as HTMLElement).closest('[data-expand-toggle]')
        ) {
          return
        }
        // Don't trigger if clicking on the checkbox itself (it has its own handler)
        if ((e.target as HTMLElement).closest('button[role="checkbox"]')) {
          return
        }

        handleParentClick()
      }}
    >
      <div className="flex flex-row gap-2 items-start w-full">
        <CheckboxComponent
          checked={isSelected}
          className="mt-0.5"
          disabled={disabled}
          onCheckedChange={() => !disabled && handleParentClick()}
        />
        <div className="flex-1 min-w-0">
          <div
            className={cn('text-sm truncate', {
              'underline decoration-dotted': !disabled,
            })}
          >
            {label}
          </div>
          {description && (
            <div className="text-xs text-muted-foreground truncate">
              {description}
            </div>
          )}
        </div>
        {children && children.length > 0 && (
          <button
            data-expand-toggle
            type="button"
            className="p-1 rounded hover:bg-muted-foreground/10 transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
          >
            <Icon
              name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
              className="h-4 w-4 text-muted-foreground"
            />
          </button>
        )}
      </div>
      {tags && tags.length > 0 && (
        <div className="flex gap-1 items-center flex-wrap">
          {tags.map((tag, index) => (
            <Chip key={`checkbox-tile-tag-${id}-${index}`} {...tag} />
          ))}
        </div>
      )}
      {children && children.length > 0 && (
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleContent>
            <div className="ml-6 flex flex-col gap-1 border-l border-border pl-2">
              {children.map((child) => (
                <div
                  key={child.id}
                  data-child-tile
                  className={cn(
                    'rounded-md flex p-1.5 flex-row gap-2 items-start',
                    {
                      'bg-accent/50': child.isSelected,
                      'hover:bg-muted cursor-pointer': !child.disabled,
                      'opacity-50 cursor-not-allowed': child.disabled,
                    },
                    s.smallTransitionIn,
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (child.disabled) return
                    // Don't trigger if clicking on the checkbox itself (it has its own handler)
                    if (
                      (e.target as HTMLElement).closest(
                        'button[role="checkbox"]',
                      )
                    ) {
                      return
                    }
                    child.handleClick && child.handleClick()
                  }}
                >
                  <CheckboxComponent
                    checked={child.isSelected}
                    className="mt-0.5"
                    disabled={child.disabled}
                    onCheckedChange={() => {
                      if (child.disabled) return
                      child.handleClick && child.handleClick()
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className={cn('text-xs truncate', {
                        'underline decoration-dotted': !child.disabled,
                      })}
                    >
                      {child.label}
                    </div>
                    {child.description && (
                      <div className="text-xs text-muted-foreground truncate">
                        {child.description}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}
